import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PublicApi } from '../constants';
import { AppResponse } from '../constants/ApiResponse';
import { MicroApiService } from '../constants/micro-api.service';

// Define a type for the keys of PublicApi
type PublicApiKeys = keyof typeof PublicApi | 'BILLING_DISCOUNT';

/**
 * Every request goes to the microservice gateway through MicroApiService.route — there is no
 * other backend. A key with no gateway action is answered in the browser (an empty result for
 * a read, an error for a write) and never leaves it. The second argument of `route` says which.
 */
@Injectable({
  providedIn: 'root',
})
export class PagesService {
  constructor(private micro: MicroApiService) { }

  getContestList(endpoint: PublicApiKeys, page: number, perPage: number, startDate?: any, endDate?: any): Observable<any> {
    return this.micro.route(endpoint, true, { page, per_page: perPage, start_date: startDate, end_date: endDate });
  }

  getParticipant(endpoint: PublicApiKeys, id?: any): Observable<any> {
    return this.micro.route(endpoint, true, undefined, id);
  }

  getCards(endpoint: PublicApiKeys, id?: any): Observable<any> {
    return this.micro.route(endpoint, true, undefined, id);
  }

  getCardDetail(endpoint: PublicApiKeys, id?: any): Observable<any> {
    return this.micro.route(endpoint, true, undefined, id);
  }

  updateCardDetail(endpoint: PublicApiKeys, id: any, data: any): Observable<any> {
    return this.micro.route(endpoint, false, data, id);
  }

  deleteCard(endpoint: PublicApiKeys, id: any): Observable<any> {
    return this.micro.route(endpoint, false, undefined, id);
  }

  deleteWithBody(endpoint: PublicApiKeys, body: any): Observable<any> {
    return this.micro.route(endpoint, false, body);
  }

  getAll(endpoint: PublicApiKeys, queryParams?: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, queryParams);
  }

  delete(endpoint: PublicApiKeys): Observable<AppResponse> {
    return this.micro.route(endpoint, false);
  }

  update(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  put(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  /** Update by id — the id travels as `extra`, the way the path segment used to. */
  putById(endpoint: PublicApiKeys, id: string | number, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model, id);
  }

  patch(endpoint: PublicApiKeys, id: string | number, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model, id);
  }

  updateWithId(endpoint: PublicApiKeys, model: any, id: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model, id);
  }

  getById(endpoint: PublicApiKeys, id: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, { id }, id);
  }

  getByCustomName(endpoint: PublicApiKeys, customName: string, id: any): Observable<AppResponse> {
    // The name and the value both go through, so the translation can put the value under
    // whichever key the action expects rather than guessing from the URL it used to build.
    return this.micro.route(endpoint, true, customName, id);
  }

  filterNonEmptyValues(obj: any): Partial<any> {
    const filteredObj: Partial<any> = {};

    for (const key in obj) {
      if (obj[key as keyof any] !== '') {
        filteredObj[key as any] = obj[key as keyof any];
      }
    }

    return filteredObj;
  }

  VerifyEmailOTPSv(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  create(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  verify(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  getByIdByQuerry(endpoint: PublicApiKeys, id: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, { id }, id);
  }

  resetPassword(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    return this.micro.route(endpoint, false, model);
  }

  getAllById(endpoint: PublicApiKeys, params?: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, params ?? {});
  }

  // Filtered list — a read, even though it carries a body
  postWithFilters(endpoint: PublicApiKeys, body: any): Observable<AppResponse> {
    return this.micro.route(endpoint, true, body);
  }
}
